// אשף "קמפיין חדש" — כל הדרך מלקוח ועד ZIP בזרימה אחת. שואל רק מה שחסר.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, waitForJob, type Job } from '../api';
import { ErrorBox, Field, Loading, useMe, useToast } from '../components/ui';
import BannerView, { fitScale } from '../components/editor/BannerView';
import VariantThumb, { useVariantCtx } from '../components/editor/VariantThumb';
import type { VariantCtx } from '../components/editor/types';
import { autofitVariant } from '../components/editor/autofit';
import Icon from '../components/Icon';
import '../quick.css';

const COMMON_SIZES: [number, number, string][] = [
  [300, 250, 'מלבן'], [728, 90, 'רצועה עליונה'], [160, 600, 'גורד שחקים'], [300, 600, 'חצי עמוד'],
  [970, 250, 'בילבורד'], [320, 50, 'מובייל רצועה'], [320, 100, 'מובייל גדול'], [336, 280, 'מלבן גדול'], [120, 600, 'גורד צר'], [468, 60, 'באנר'],
];
const STEPS = ['לקוח', 'מיתוג', 'אתרים וגדלים', 'טקסטים', 'באנר ראשי', 'כל הגדלים והורדה'];
type Size = { width: number; height: number };
interface Colors { primary: string; cta: string; text: string; background: string }
interface MediaRow { id: string; name: string; sizes: Size[]; zipMaxKb: number | null; specStatus: string | null; is_demo: boolean }
export interface MediaPick { siteId?: string; newName?: string; name: string; sizes: Size[]; zipMaxKb: number; confirmSpec: boolean; known: Size[] }
interface Setup { campaignId: string; setId: string; variants: { id: string; width: number; height: number; site: string }[]; masterVariantId: string }

const sizeKey = (s: Size) => `${s.width}x${s.height}`;

export default function QuickCampaign() {
  const me = useMe();
  const toast = useToast();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // שלב 1 — לקוח
  const [clients, setClients] = useState<{ id: string; name: string }[] | null>(null);
  const [clientId, setClientId] = useState('');
  const [newClient, setNewClient] = useState('');
  // שלב 2 — מיתוג
  const [brandInfo, setBrandInfo] = useState<{ client: any; brands: any[] } | null>(null);
  const [brandMode, setBrandMode] = useState<'existing' | 'new'>('existing');
  const [brandId, setBrandId] = useState('');
  const [logo, setLogo] = useState<{ id: string; url: string } | null>(null);
  const [colors, setColors] = useState<Colors>({ primary: '#1F3A5F', cta: '#E4572E', text: '#111111', background: '#FFFFFF' });
  // שלב 3 — מדיה
  const [media, setMedia] = useState<MediaRow[] | null>(null);
  const [picks, setPicks] = useState<MediaPick[]>([]);
  const [newSite, setNewSite] = useState('');
  // שלב 4 — טקסטים
  const [txt, setTxt] = useState({ name: '', headline: '', subline: '', cta: 'לפרטים', disclaimer: '', url: '', prompt: '' });
  const [image, setImage] = useState<{ id: string; url: string } | null>(null);
  const [autoImage, setAutoImage] = useState(true);
  // שלבים 5–6
  const [setup, setSetup] = useState<Setup | null>(null);
  // מצב "סט חדש לקמפיין קיים": ?add=<campaignId> — מדלגים על לקוח ומיתוג
  const [existing, setExisting] = useState<{ id: string; name: string } | null>(null);

  const run = async <T,>(label: string, f: () => Promise<T>): Promise<T | null> => {
    setBusy(label); setError(null);
    try { return await f(); } catch (e: any) { setError(e.message); return null; } finally { setBusy(null); }
  };

  // חידוש עבודה אחרי רענון: ?set=&master=&campaign=
  useEffect(() => {
    const s = params.get('set'), m = params.get('master'), c = params.get('campaign');
    if (s && m && c) {
      api.get(`/api/banner-sets/${s}`).then((r) => {
        setSetup({ setId: s, masterVariantId: m, campaignId: c, variants: r.variants.map((v: any) => ({ id: v.id, width: v.width, height: v.height, site: v.media_name })) });
        setStep(params.get('step') === '6' ? 5 : 4);
      }).catch(() => {});
    }
    const add = params.get('add');
    if (add && !s) {
      api.get(`/api/campaigns/${add}`).then(async (d) => {
        const c = d.campaign, cp = c.copy ?? {};
        setClientId(c.client_id);
        setExisting({ id: c.id, name: c.name });
        setTxt((t) => ({ ...t, name: c.name, url: c.default_url ?? '', headline: cp.headlines?.[0]?.text ?? '', subline: cp.texts?.[0]?.text ?? '', cta: cp.ctas?.[0]?.text ?? 'לפרטים', disclaimer: cp.disclaimer?.text ?? '' }));
        setMedia(await api.get('/api/quick/media'));
        setStep(2);
      }).catch((e) => setError(e.message));
    }
    api.get('/api/clients').then(setClients).catch((e) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── מעברים ──
  const goBrand = async () => {
    let id = clientId;
    if (!id) {
      if (!newClient.trim()) { setError('בחרו לקוח או הקלידו שם ללקוח חדש'); return; }
      const c = await run('יוצר לקוח…', () => api.post('/api/clients', { name: newClient.trim() }));
      if (!c) return;
      id = c.id; setClientId(id);
    }
    const info = await run('טוען מיתוג…', () => api.get(`/api/quick/client/${id}`));
    if (!info) return;
    setBrandInfo(info);
    if (info.brands.length) { setBrandMode('existing'); setBrandId(info.brands[0].id); } else setBrandMode('new');
    setStep(1);
  };

  const uploadLogo = async (file: File) => {
    const fd = new FormData();
    fd.append('clientId', clientId); fd.append('kind', 'logo'); fd.append('file', file);
    const r = await run('מעלה לוגו…', () => api.upload('/api/assets/upload', fd));
    if (!r) return;
    setLogo({ id: r[0].id, url: r[0].url });
    const pal = await run('מציע צבעים מהלוגו…', () => api.post('/api/quick/palette', { assetId: r[0].id }));
    if (pal) setColors(pal.suggested);
  };

  const goMedia = async () => {
    if (brandMode === 'new' && !logo) { setError('העלו לוגו כדי ליצור מיתוג'); return; }
    if (brandMode === 'existing' && !brandId) { setError('בחרו מיתוג'); return; }
    if (!media) { const m = await run('טוען אתרים…', () => api.get('/api/quick/media')); if (!m) return; setMedia(m); }
    setStep(2);
  };

  const toggleSite = (m: MediaRow) => {
    if (picks.some((p) => p.siteId === m.id)) { setPicks(picks.filter((p) => p.siteId !== m.id)); return; }
    setPicks([...picks, { siteId: m.id, name: m.name, sizes: [...m.sizes], known: m.sizes, zipMaxKb: m.zipMaxKb ?? 150, confirmSpec: me.canApproveSpecs }]);
  };
  const addNewSite = () => {
    const n = newSite.trim();
    if (!n) return;
    setPicks([...picks, { newName: n, name: n, sizes: [], known: [], zipMaxKb: 150, confirmSpec: me.canApproveSpecs }]);
    setNewSite('');
  };
  const updatePick = (i: number, p: Partial<MediaPick>) => setPicks(picks.map((x, j) => (j === i ? { ...x, ...p } : x)));

  const goTexts = () => {
    if (!picks.length) { setError('בחרו לפחות אתר אחד'); return; }
    const empty = picks.find((p) => !p.sizes.length);
    if (empty) { setError(`בחרו גדלים לאתר "${empty.name}"`); return; }
    setError(null);
    setStep(3);
  };

  const uploadImage = async (file: File) => {
    const fd = new FormData();
    fd.append('clientId', clientId); fd.append('kind', 'image'); fd.append('file', file);
    const r = await run('מעלה תמונה…', () => api.upload('/api/assets/upload', fd));
    if (r) setImage({ id: r[0].id, url: r[0].url });
  };

  const create = async () => {
    if (!txt.name.trim() || !txt.headline.trim() || !txt.cta.trim() || !txt.url.trim()) { setError(existing ? 'כותרת וטקסט כפתור — חובה' : 'שם קמפיין, כותרת, כפתור וכתובת נחיתה — חובה'); return; }
    try { new URL(txt.url); } catch { setError('כתובת הנחיתה לא תקינה — צריכה להתחיל ב-https://'); return; }
    if (existing) {
      const r = await run('יוצר סט חדש…', () => api.post<Setup>('/api/quick/add-set', {
        campaignId: existing.id,
        media: picks.map((p) => ({ siteId: p.siteId, newName: p.newName, sizes: p.sizes, zipMaxKb: p.zipMaxKb, confirmSpec: p.confirmSpec })),
        copy: { headline: txt.headline.trim(), subline: txt.subline.trim(), cta: txt.cta.trim(), disclaimer: txt.disclaimer.trim() },
        imageAssetId: image?.id ?? null, prompt: txt.prompt,
      }));
      if (!r) return;
      setSetup(r);
      setParams({ set: r.setId, master: r.masterVariantId, campaign: r.campaignId }, { replace: true });
      setStep(4);
      return;
    }
    let imageId = image?.id ?? null;
    // אין תמונה? ה-AI לא מצייר תמונות אמיתיות, ולכן יוצרים תמונת צילום מהנושא לפני העיצוב
    if (!imageId && autoImage && !existing) {
      setBusy('יוצר תמונה ריאליסטית לבאנר (עד חצי דקה)…');
      try {
        const res = await fetch('/api/quick/generate-image', { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ clientId, prompt: `${txt.prompt.trim() || txt.headline.trim()}${txt.subline.trim() ? `. ${txt.subline.trim()}` : ''}`, shape: 'landscape', modest: true, style: 'photo' }), signal: AbortSignal.timeout(150_000) });
        const out = await res.json().catch(() => null);
        if (res.ok) { imageId = out.id; setImage(out); } else setError(`לא נוצרה תמונה אוטומטית (${out?.error ?? res.status}). ממשיכים בלי תמונה.`);
      } catch { setError('יצירת התמונה האוטומטית לקחה יותר מדי. ממשיכים בלי תמונה — אפשר להעלות תמונה או לנסות שוב.'); }
      setBusy(null);
    }
    const body = {
      clientId,
      brand: brandMode === 'existing' ? { mode: 'existing', brandId } : { mode: 'new', logoAssetId: logo!.id, colors },
      media: picks.map((p) => ({ siteId: p.siteId, newName: p.newName, sizes: p.sizes, zipMaxKb: p.zipMaxKb, confirmSpec: p.confirmSpec })),
      campaign: { name: txt.name.trim(), headline: txt.headline.trim(), subline: txt.subline.trim(), cta: txt.cta.trim(), disclaimer: txt.disclaimer.trim(), url: txt.url.trim() },
      imageAssetId: imageId, prompt: txt.prompt,
    };
    const r = await run('מקים קמפיין, אתרים וגדלים…', () => api.post<Setup>('/api/quick/setup', body));
    if (!r) return;
    setSetup(r);
    setParams({ set: r.setId, master: r.masterVariantId, campaign: r.campaignId }, { replace: true });
    setStep(4);
  };

  return (
    <div className="quick">
      <div className="row between"><h1 style={{ margin: 0 }}>{existing ? `סט חדש: ${existing.name}` : 'קמפיין חדש'}</h1>{setup && <Link to={`/campaigns/${setup.campaignId}`}>לכרטיס הקמפיין</Link>}</div>
      <ol className="q-steps" aria-label="שלבים">
        {STEPS.map((s, i) => <li key={s} className={i === step ? 'cur' : i < step ? 'done' : ''} aria-current={i === step ? 'step' : undefined}>{i + 1}. {s}</li>)}
      </ol>
      <div aria-live="polite">{busy && <div className="alert info"><span className="spinner" /> {busy}</div>}</div>
      <ErrorBox error={error} />

      {step === 0 && (
        <section className="card stack q-card">
          <h2>לאיזה לקוח?</h2>
          {!clients ? <Loading /> : clients.length > 0 && (
            <Field label="לקוח קיים">
              <select value={clientId} onChange={(e) => { setClientId(e.target.value); setNewClient(''); }}>
                <option value="">— בחרו —</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
          )}
          <Field label={clients?.length ? 'או לקוח חדש' : 'שם הלקוח'}><input value={newClient} onChange={(e) => { setNewClient(e.target.value); setClientId(''); }} placeholder="לדוגמה: משכנות שיא" /></Field>
          <div className="q-nav"><button className="btn primary" onClick={goBrand} disabled={!!busy}>המשך</button></div>
        </section>
      )}

      {step === 1 && brandInfo && (
        <section className="card stack q-card">
          <h2>מיתוג — {brandInfo.client.name}</h2>
          {brandInfo.brands.length > 0 && (
            <div className="stack">
              <div>ללקוח יש מיתוג שמור. להשתמש בו?</div>
              {brandInfo.brands.map((b) => (
                <label key={b.id} className={`q-brand ${brandMode === 'existing' && brandId === b.id ? 'sel' : ''}`}>
                  <input type="radio" name="brand" checked={brandMode === 'existing' && brandId === b.id} onChange={() => { setBrandMode('existing'); setBrandId(b.id); }} />
                  {b.logoUrl ? <img src={b.logoUrl} alt="" className="thumb" /> : <span className="thumb" />}
                  <span><strong>{b.name}</strong> <span className="small muted">גרסה {b.version}</span>
                    <span className="row" style={{ gap: 4 }}>{b.colors.map((c: any) => <span key={c.hex + c.role} className="swatch" style={{ background: c.hex }} title={`${c.role} ${c.hex}`} />)}</span>
                    <span className="small muted">{b.fonts.join(', ') || 'אין פונט — ישמש פונט מערכת'}</span></span>
                </label>
              ))}
              <label className={`q-brand ${brandMode === 'new' ? 'sel' : ''}`}><input type="radio" name="brand" checked={brandMode === 'new'} onChange={() => setBrandMode('new')} /> <span>לא — מיתוג חדש</span></label>
            </div>
          )}
          {brandMode === 'new' && (
            <div className="stack">
              {!brandInfo.brands.length && <div className="alert info">ללקוח עוד אין מיתוג. העלו לוגו — המערכת תציע צבעים מתוכו. פונט עברי (Heebo, חינמי ומותר להטמעה) יצורף אוטומטית.</div>}
              <Field label="לוגו (PNG / SVG / JPG)"><input type="file" accept="image/png,image/svg+xml,image/jpeg,image/webp" onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0])} /></Field>
              {logo && <img src={logo.url} alt="הלוגו שהועלה" style={{ maxWidth: 220, maxHeight: 100, objectFit: 'contain', background: '#f3f3f3', padding: 8, borderRadius: 6 }} />}
              {logo && (
                <div className="grid cols-2">
                  {([['primary', 'צבע ראשי'], ['cta', 'צבע כפתור'], ['text', 'צבע טקסט'], ['background', 'צבע רקע']] as const).map(([k, l]) => (
                    <Field key={k} label={l}><span className="row"><input type="color" value={colors[k]} onChange={(e) => setColors({ ...colors, [k]: e.target.value.toUpperCase() })} /><input className="ltr mono" style={{ width: 100 }} value={colors[k]} onChange={(e) => setColors({ ...colors, [k]: e.target.value })} /></span></Field>
                  ))}
                </div>
              )}
              <div className="small muted">יש אתר או מסמך מיתוג? אפשר גם <Link to={`/brand-import?clientId=${clientId}`}>לחלץ שפה גרפית מהם</Link> ולחזור לכאן.</div>
            </div>
          )}
          <div className="q-nav"><button className="btn" onClick={() => setStep(0)}>חזרה</button><button className="btn primary" onClick={goMedia} disabled={!!busy}>המשך</button></div>
        </section>
      )}

      {step === 2 && media && (
        <section className="card stack q-card">
          <h2>באילו אתרים הקמפיין ירוץ?</h2>
          {media.length > 0 && (
            <div className="q-sites">
              {media.map((m) => (
                <label key={m.id} className={`q-brand ${picks.some((p) => p.siteId === m.id) ? 'sel' : ''}`}>
                  <input type="checkbox" checked={picks.some((p) => p.siteId === m.id)} onChange={() => toggleSite(m)} />
                  <span><strong>{m.name}</strong>{m.is_demo && <span className="badge warn"> הדגמה</span>}<span className="small muted ltr"> {m.sizes.map(sizeKey).join(' · ') || 'אין מידות שמורות'}</span></span>
                </label>
              ))}
            </div>
          )}
          <div className="row"><input value={newSite} onChange={(e) => setNewSite(e.target.value)} placeholder="אתר חדש — לדוגמה: כל רגע" onKeyDown={(e) => { if (e.key === 'Enter') addNewSite(); }} aria-label="שם אתר חדש" /><button className="btn" onClick={addNewSite}>הוספה</button></div>
          {picks.map((p, i) => <SitePick key={(p.siteId ?? '') + p.name} p={p} canApprove={me.canApproveSpecs} onChange={(x) => updatePick(i, x)} onRemove={() => setPicks(picks.filter((_, j) => j !== i))} />)}
          <div className="q-nav"><button className="btn" onClick={() => (existing ? nav(`/campaigns/${existing.id}`) : setStep(1))}>חזרה</button><button className="btn primary" onClick={goTexts}>המשך</button></div>
        </section>
      )}

      {step === 3 && (
        <section className="card stack q-card">
          <h2>מה כתוב בבאנר?</h2>
          {!existing && <Field label="שם הקמפיין *"><input value={txt.name} onChange={(e) => setTxt({ ...txt, name: e.target.value })} placeholder="לדוגמה: השקה סוכות" /></Field>}
          {existing && <div className="alert info small">הטקסטים נלקחו מהקמפיין. שינוי כאן יעדכן אותם בקמפיין כולו.</div>}
          <Field label="כותרת *"><input value={txt.headline} onChange={(e) => setTxt({ ...txt, headline: e.target.value })} placeholder="דירות 4–5 חדרים בהרצליה" /></Field>
          <Field label="משפט משנה"><input value={txt.subline} onChange={(e) => setTxt({ ...txt, subline: e.target.value })} placeholder="החל מ-2,950,000 ₪" /></Field>
          <div className="grid cols-2">
            <Field label="טקסט הכפתור *"><input value={txt.cta} onChange={(e) => setTxt({ ...txt, cta: e.target.value })} /></Field>
            {!existing && <Field label="כתובת הנחיתה *" hint="לשם הלחיצה תוביל; קישורי מעקב יוטמעו אוטומטית"><input className="ltr" value={txt.url} onChange={(e) => setTxt({ ...txt, url: e.target.value })} placeholder="https://" /></Field>}
          </div>
          <Field label="גילוי נאות / הערה קטנה"><input value={txt.disclaimer} onChange={(e) => setTxt({ ...txt, disclaimer: e.target.value })} placeholder="ההדמיה להמחשה בלבד" /></Field>
          <Field label="תמונה לבאנר (לא חובה)"><input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} /></Field>
          {image && <img src={image.url} alt="" style={{ maxWidth: 220, maxHeight: 120, objectFit: 'cover', borderRadius: 6 }} />}
          <AiImageBox clientId={clientId} seed={txt.prompt} onImage={(i) => setImage(i)} />
          {!image && !existing && <label className="row small"><input type="checkbox" checked={autoImage} onChange={(e) => setAutoImage(e.target.checked)} /> אין תמונה? ליצור אוטומטית תמונת צילום ריאליסטית לפי הנושא (כחצי דקה, כ-0.17$)</label>}
          <div className="alert info small">ה-AI מעצב את הפריסה, הצבעים והתנועה. <strong>הוא לא מצייר תמונות אמיתיות</strong> (בלי תמונה מצורפת הוא רק משרטט צורות פשוטות). לתמונה אמיתית: העלו תמונה או השתמשו ב"ליצור תמונה ב-AI".</div>
          <Field label="הנחיה לעיצוב (לא חובה)"><textarea value={txt.prompt} onChange={(e) => setTxt({ ...txt, prompt: e.target.value })} placeholder="לדוגמה: אווירה יוקרתית, דגש על המחיר, תנועה עדינה (לא לתיאור תמונה)" /></Field>
          <div className="q-nav"><button className="btn" onClick={() => setStep(2)}>חזרה</button><button className="btn primary" onClick={create} disabled={!!busy}>צור באנר</button></div>
        </section>
      )}

      {step === 4 && setup && <MasterStep setup={setup} onApproved={() => { setParams({ set: setup.setId, master: setup.masterVariantId, campaign: setup.campaignId, step: '6' }, { replace: true }); setStep(5); }} onError={setError} />}
      {step === 5 && setup && <AllSizesStep setup={setup} onError={setError} toast={toast} nav={nav} />}
    </div>
  );
}

export function SitePick({ p, canApprove, onChange, onRemove }: { p: MediaPick; canApprove: boolean; onChange: (x: Partial<MediaPick>) => void; onRemove: () => void }) {
  const [w, setW] = useState('');
  const [h, setH] = useState('');
  const has = (s: Size) => p.sizes.some((x) => x.width === s.width && x.height === s.height);
  const toggle = (s: Size) => onChange({ sizes: has(s) ? p.sizes.filter((x) => !(x.width === s.width && x.height === s.height)) : [...p.sizes, s] });
  const extra = p.known.filter((k) => !COMMON_SIZES.some(([cw, ch]) => cw === k.width && ch === k.height));
  return (
    <div className="q-site">
      <div className="row between"><strong>{p.name}</strong><button className="btn small" onClick={onRemove}>הסרה</button></div>
      {!p.known.length && <div className="small">באילו גדלים האתר מקבל באנרים? סמנו לפי המפרט של האתר.</div>}
      <div className="q-sizes">
        {[...COMMON_SIZES.map(([cw, ch, l]) => ({ width: cw, height: ch, l })), ...extra.map((e) => ({ ...e, l: '' }))].map((s) => (
          <button key={sizeKey(s)} type="button" className={`q-size ${has(s) ? 'on' : ''}`} aria-pressed={has(s)} onClick={() => toggle(s)}>
            <span className="ltr">{s.width}×{s.height}</span>{s.l && <span className="small">{s.l}</span>}
          </button>
        ))}
      </div>
      <div className="row">
        <input className="ltr" style={{ width: 70 }} inputMode="numeric" placeholder="רוחב" value={w} onChange={(e) => setW(e.target.value)} aria-label="רוחב" />×
        <input className="ltr" style={{ width: 70 }} inputMode="numeric" placeholder="גובה" value={h} onChange={(e) => setH(e.target.value)} aria-label="גובה" />
        <button className="btn small" onClick={() => { const s = { width: Number(w), height: Number(h) }; if (s.width >= 10 && s.height >= 10 && !has(s)) { onChange({ sizes: [...p.sizes, s] }); setW(''); setH(''); } }}>גודל אחר</button>
      </div>
      <div className="row">
        <Field label="משקל מרבי ל-ZIP (KB)" hint="150 הוא ערך נפוץ — אם במפרט של האתר כתוב אחרת, עדכנו"><input type="number" className="ltr" style={{ width: 100 }} value={p.zipMaxKb} onChange={(e) => onChange({ zipMaxKb: Number(e.target.value) })} /></Field>
        {canApprove && <label className="row small"><input type="checkbox" checked={p.confirmSpec} onChange={(e) => onChange({ confirmSpec: e.target.checked })} /> המידות והמשקל נכונים לאתר הזה (מאפשר יצוא מוכן למסירה)</label>}
      </div>
    </div>
  );
}

/** שניות שחלפו מאז שהפעולה התחילה — להצגת התקדמות אמיתית ולזיהוי המתנה ארוכה מדי */
function useElapsed(active: boolean) {
  const [s, setS] = useState(0);
  useEffect(() => {
    if (!active) { setS(0); return; }
    const t0 = Date.now();
    const id = setInterval(() => setS(Math.round((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(id);
  }, [active]);
  return s;
}

function MasterStep({ setup, onApproved, onError }: { setup: Setup; onApproved: () => void; onError: (e: string | null) => void }) {
  const [rev, setRev] = useState(0);
  const { ctx } = useVariantCtx(setup.masterVariantId, rev);
  const [job, setJob] = useState<Job | null>(null);
  const [state, setState] = useState<'idle' | 'gen' | 'edit'>('idle');
  const [instr, setInstr] = useState('');
  const [replay, setReplay] = useState(0);
  const generated = ctx?.revision && ctx.revision.source !== 'template';
  const elapsed = useElapsed(state !== 'idle');
  const stop = useRef<(() => void) | null>(null);
  const [opts, setOpts] = useState<{ baseRevision: number; options: { key: string; label: string; doc: any }[]; assets: Record<string, any> } | null>(null);

  /** כמה כיווני עיצוב שונים במקביל; בוחרים אחד ורק אז נשמר */
  const showOptions = async () => {
    setState('gen'); onError(null);
    try {
      const { jobId } = await api.post(`/api/variants/${setup.masterVariantId}/ai-options`, { prompt: '', count: 3 });
      const j = await waitForJob(jobId, setJob);
      if (j.status !== 'succeeded') { onError(`יצירת הכיוונים נכשלה: ${j.error ?? ''}`); return; }
      const ids = [...new Set<string>(j.result.options.flatMap((o: any) => o.doc.assetIds))];
      const assets = await api.post('/api/assets/preview-map', { ids });
      setOpts({ baseRevision: j.result.baseRevision, options: j.result.options, assets: { ...(ctx?.assets ?? {}), ...assets } });
      if (j.result.errors?.length) onError(`${j.result.errors.length} כיוונים לא נוצרו: ${j.result.errors[0]}`);
    } catch (e: any) { onError(e.message); } finally { setState('idle'); }
  };
  const chooseOption = async (o: { label: string; doc: any }) => {
    if (!opts) return;
    setState('edit'); onError(null);
    try {
      await api.post(`/api/variants/${setup.masterVariantId}/adapt/apply`, { baseRevision: opts.baseRevision, doc: o.doc, summary: `כיוון עיצוב: ${o.label}` });
      await autofitVariant(setup.masterVariantId);
      setOpts(null); setRev((x) => x + 1);
    } catch (e: any) { onError(e.message); } finally { setState('idle'); }
  };

  const generate = async () => {
    setState('gen'); onError(null);
    try {
      const { jobId } = await api.post('/api/banner-sets/generate', { setId: setup.setId, variantIds: [setup.masterVariantId], idempotencyKey: crypto.randomUUID() });
      const cancelled = new Promise<'stop'>((res) => { stop.current = () => res('stop'); });
      const j = await Promise.race([waitForJob(jobId, setJob), cancelled]);
      if (j === 'stop') { setRev((x) => x + 1); return; }
      if (j.status !== 'succeeded') onError(`היצירה נכשלה: ${j.error ?? ''}. הבאנר נשאר בתבנית הפתיחה — אפשר לנסות שוב או לאשר כמו שהוא.`);
      else await autofitVariant(setup.masterVariantId);
      setRev((x) => x + 1);
    } catch (e: any) { onError(e.message); } finally { setState('idle'); }
  };
  // יצירה אוטומטית בכניסה לשלב (פעם אחת)
  useEffect(() => { if (ctx && ctx.revision?.source === 'template' && state === 'idle' && !job) generate(); /* eslint-disable-next-line */ }, [ctx]);

  const change = async () => {
    if (!instr.trim() || !ctx) return;
    setState('edit'); onError(null);
    try {
      const base = ctx.variant.current_revision;
      const { jobId } = await api.post(`/api/variants/${setup.masterVariantId}/ai-edit`, { instruction: instr.trim(), baseRevision: base });
      const j = await waitForJob(jobId, setJob);
      if (j.status !== 'succeeded') { onError(`השינוי לא בוצע: ${j.error ?? ''}`); return; }
      await api.post(`/api/variants/${setup.masterVariantId}/ai-edit/apply`, { baseRevision: base, ops: j.result.proposal.ops, summary: j.result.proposal.summary });
      await autofitVariant(setup.masterVariantId);
      setInstr('');
      setRev((x) => x + 1);
    } catch (e: any) { onError(e.message); } finally { setState('idle'); }
  };

  if (!ctx?.revision) return <Loading what="טוען באנר" />;
  const d = ctx.revision.doc;
  return (
    <section className="card stack q-card">
      <h2>הבאנר הראשי ({d.width}×{d.height})</h2>
      <div className="small muted">מאשרים קודם גודל אחד. אחרי האישור המערכת תתאים אותו לכל שאר הגדלים.</div>
      {state !== 'idle' && (
        <div className="alert info row between" role="status">
          <span><span className="spinner" /> {state === 'gen' ? 'ה-AI מעצב את הבאנר' : 'מבצע את השינוי'}… {elapsed} שניות {elapsed < 60 ? '(בדרך כלל עד דקה)' : ''}</span>
          {elapsed >= 75 && <span className="row"><span className="small">לוקח יותר מהרגיל.</span><button className="btn small" onClick={() => stop.current?.()}>בדיקה מחדש מהשרת</button></span>}
        </div>
      )}
      <div className="q-master">
        <BannerView key={`${rev}-${replay}`} doc={d} assets={ctx.assets} autoplay at={0} scale={fitScale(d.width, d.height, 640, 420)} label={`באנר ${d.width}x${d.height}`} />
      </div>
      {opts && (
        <div className="stack" style={{ gap: 12 }}>
          <strong>בחרו כיוון עיצוב</strong>
          <div className="q-options">
            {opts.options.map((o) => (
              <div key={o.key} className="q-option">
                <BannerView doc={o.doc} assets={opts.assets} autoplay at={0} scale={fitScale(o.doc.width, o.doc.height, 300, 240)} label={`כיוון ${o.label}`} />
                <button className="btn primary small" onClick={() => chooseOption(o)} disabled={state !== 'idle'}>{o.label}</button>
              </div>
            ))}
          </div>
          <button className="btn small" onClick={() => setOpts(null)}>ביטול, להישאר עם הקיים</button>
        </div>
      )}
      <div className="row"><button className="btn small" onClick={() => setReplay((x) => x + 1)}>▶ ניגון חוזר</button>{ctx.revision.summary && <span className="small muted">{ctx.revision.summary}</span>}</div>
      <div className="row" style={{ alignItems: 'flex-end' }}>
        <Field label="רוצים לשנות משהו? כתבו במילים"><input style={{ minWidth: 340 }} value={instr} onChange={(e) => setInstr(e.target.value)} placeholder="לדוגמה: החלף את הכותרת לפונט סריפי, הגדל אותה ב-15 אחוז, הוסף פס כחול מתחת" onKeyDown={(e) => { if (e.key === 'Enter') change(); }} /></Field>
        <button className="btn" onClick={change} disabled={state !== 'idle' || !instr.trim()}>{state === 'edit' ? 'מבצע…' : 'בצע שינוי'}</button>
        <button className="btn" onClick={showOptions} disabled={state !== 'idle'}>3 כיווני עיצוב</button>
        <button className="btn" onClick={generate} disabled={state !== 'idle'}>עיצוב אחר</button>
        <Link className="btn" to={`/variants/${setup.masterVariantId}/edit`}>עריכה ידנית</Link>
      </div>
      <div className="q-nav"><button className="btn primary" onClick={onApproved} disabled={state !== 'idle' || !generated}>מאשר — התאם לכל הגדלים</button></div>
    </section>
  );
}

function AllSizesStep({ setup, onError, toast, nav }: { setup: Setup; onError: (e: string | null) => void; toast: (t: string, k?: 'ok' | 'err') => void; nav: (to: string) => void }) {
  const others = useMemo(() => setup.variants.filter((v) => v.id !== setup.masterVariantId), [setup]);
  const [status, setStatus] = useState<Record<string, 'wait' | 'run' | 'ok' | 'err'>>({});
  const [rev, setRev] = useState(0);
  const [phase, setPhase] = useState<'adapt' | 'ready' | 'export' | 'done'>('adapt');
  const [warnings, setWarnings] = useState<{ variant: string; codes: string[]; msgs: string[] }[] | null>(null);
  const [ack, setAck] = useState(false);
  const [links, setLinks] = useState<any[]>([]);
  const [bundle, setBundle] = useState<{ url: string; count: number } | null>(null);
  const [kind, setKind] = useState<'delivery' | 'draft'>('delivery');

  // התאמה לכל הגדלים — פעם אחת, אחד אחרי השני
  useEffect(() => {
    let alive = true;
    (async () => {
      const done = sessionStorage.getItem(`q-adapted-${setup.setId}`) === '1';
      if (!done) {
        for (const v of others) {
          if (!alive) return;
          setStatus((s) => ({ ...s, [v.id]: 'run' }));
          try {
            const { jobId } = await api.post(`/api/variants/${v.id}/adapt`, { fromVariantId: setup.masterVariantId, idempotencyKey: crypto.randomUUID() });
            const j = await waitForJob(jobId);
            if (j.status !== 'succeeded') throw new Error(j.error ?? 'נכשל');
            await api.post(`/api/variants/${v.id}/adapt/apply`, { baseRevision: j.result.baseRevision, doc: j.result.preview, summary: 'התאמה מהבאנר הראשי' });
            await autofitVariant(v.id);
            setStatus((s) => ({ ...s, [v.id]: 'ok' }));
          } catch (e: any) {
            setStatus((s) => ({ ...s, [v.id]: 'err' }));
            onError(`ההתאמה ל-${v.width}×${v.height} לא הצליחה (${e.message}). אפשר לפתוח אותו בעורך.`);
          }
          setRev((x) => x + 1);
        }
        try { sessionStorage.setItem(`q-adapted-${setup.setId}`, '1'); } catch { /* לא קריטי */ }
      }
      // קישורי מעקב לכל באנר
      try { const l = await api.post(`/api/campaigns/${setup.campaignId}/tracking-links/generate`, { existingUtmPolicy: 'replace' }); setLinks(l.rows); } catch (e: any) { onError(e.message); }
      // אזהרות לפני יצוא
      const ws = [];
      let blockedDelivery = false;
      for (const v of setup.variants) {
        try {
          const val = await api.post(`/api/variants/${v.id}/validate`, {});
          const warn = val.checks.filter((c: any) => !c.passed && c.severity === 'warning');
          if (val.checks.some((c: any) => !c.passed && c.severity === 'error')) blockedDelivery = true;
          if (warn.length) ws.push({ variant: `${v.width}×${v.height}`, codes: warn.map((c: any) => c.code), msgs: warn.map((c: any) => c.message) });
        } catch { /* תוצג בשלב היצוא */ }
      }
      if (alive) { setWarnings(ws); if (blockedDelivery) setKind('draft'); setPhase('ready'); }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportAll = async () => {
    setPhase('export'); onError(null);
    try {
      const codes = new Map<string, string[]>();
      for (const v of setup.variants) {
        const val = await api.post(`/api/variants/${v.id}/validate`, {});
        codes.set(v.id, val.checks.filter((c: any) => !c.passed && c.severity === 'warning').map((c: any) => c.code));
      }
      let failed = 0;
      for (const v of setup.variants) {
        const r = await api.post(`/api/variants/${v.id}/export`, { kind, ackWarnings: kind === 'delivery' ? codes.get(v.id) : undefined, idempotencyKey: crypto.randomUUID() });
        await waitForJob(r.jobId);
        let e = await api.get(`/api/exports/${r.exportId}`);
        // אזהרות שמתגלות רק אחרי בנייה (בדיקת דפדפן) — המשתמש כבר אישר "ראיתי את האזהרות"; מאשרים אותן ובונים שוב
        const late = (e.check_results ?? []).filter((c: any) => !c.passed && c.severity === 'warning').map((c: any) => c.code);
        const onlyAck = e.status === 'blocked' && (e.check_results ?? []).every((c: any) => c.passed || c.severity !== 'error' || c.code === 'warnings_not_acknowledged');
        if (kind === 'delivery' && onlyAck && late.length) {
          const r2 = await api.post(`/api/variants/${v.id}/export`, { kind, ackWarnings: [...new Set([...(codes.get(v.id) ?? []), ...late])], idempotencyKey: crypto.randomUUID() });
          await waitForJob(r2.jobId);
          e = await api.get(`/api/exports/${r2.exportId}`);
        }
        if (e.status !== 'ready') failed++;
      }
      const b = await api.post(`/api/quick/bundle/${setup.setId}`);
      setBundle(b);
      setPhase('done');
      if (failed) onError(`${failed} באנרים לא עברו את הבדיקות ליצוא ${kind === 'delivery' ? 'למסירה' : ''} — פרטים ב"בדיקות ויצוא".`);
      else toast('כל הבאנרים מוכנים להורדה');
    } catch (e: any) { onError(e.message); setPhase('ready'); }
  };

  return (
    <section className="card stack q-card">
      <h2>כל הגדלים</h2>
      <div className="q-grid">
        {setup.variants.map((v) => <SizeCard key={v.id} id={v.id} site={v.site} st={v.id === setup.masterVariantId ? 'ok' : status[v.id] ?? 'wait'} rev={rev} />)}
      </div>
      {phase === 'adapt' && <div className="alert info"><span className="spinner" /> מתאים את הבאנר לכל הגדלים…</div>}
      {links.length > 0 && (
        <details>
          <summary>קישורי מעקב ({links.length}) — מוטמעים אוטומטית בכל באנר</summary>
          <table className="table small"><tbody>{links.map((l) => <tr key={l.variantId}><td>{l.mediaName}</td><td className="ltr">{l.width}×{l.height}</td><td className="ltr mono" style={{ wordBreak: 'break-all' }}>{l.resolvedUrl ?? l.issues.join(', ')}</td></tr>)}</tbody></table>
        </details>
      )}
      {phase !== 'adapt' && warnings && (
        <div className="stack">
          {warnings.length > 0 && (
            <div className="alert warn">
              <strong>לפני ההורדה, שימו לב:</strong>
              <ul className="small">{warnings.flatMap((w) => w.msgs.map((m, i) => <li key={w.variant + i}>{w.variant}: {m}</li>))}</ul>
              <label className="row"><input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} /> ראיתי את האזהרות</label>
            </div>
          )}
          <div className="row">
            <label className="row small"><input type="radio" checked={kind === 'delivery'} onChange={() => setKind('delivery')} /> מוכן למסירה לאתר</label>
            <label className="row small"><input type="radio" checked={kind === 'draft'} onChange={() => setKind('draft')} /> טיוטה (לבדיקה פנימית)</label>
          </div>
          {phase !== 'done' && <button className="btn primary" onClick={exportAll} disabled={phase === 'export' || (warnings.length > 0 && kind === 'delivery' && !ack)}>{phase === 'export' ? 'בודק ובונה חבילות…' : 'ייצוא והורדה של כל הבאנרים'}</button>}
          {bundle && <a className="btn primary" href={bundle.url}>⬇ הורדת {bundle.count} באנרים (ZIP)</a>}
          <div className="row small"><button className="btn small" onClick={() => nav(`/exports?setId=${setup.setId}`)}>פירוט בדיקות</button><button className="btn small" onClick={() => nav('/demo')}>לראות באתר דמו</button></div>
        </div>
      )}
    </section>
  );
}

function SizeCard({ id, site, st, rev }: { id: string; site: string; st: string; rev: number }) {
  const { ctx } = useVariantCtx(id, `${rev}-${st}`);
  const label = { wait: 'ממתין', run: 'מתאים…', ok: 'מוכן', err: 'לבדיקה ידנית' }[st] ?? st;
  return (
    <div className={`q-size-card ${st}`}>
      <VariantThumb ctx={ctx as VariantCtx | null} maxW={260} maxH={220} />
      <div className="row between small"><span className="ltr">{ctx ? `${ctx.variant.width}×${ctx.variant.height}` : ''} · {site}</span><span className={`badge ${st === 'ok' ? 'ok' : st === 'err' ? 'err' : 'info'}`}>{label}</span></div>
      <Link className="small" to={`/variants/${id}/edit`}>עריכה</Link>
    </div>
  );
}

/** יצירת תמונה ב-AI מפרומפט מסודר. התמונה נשמרת כנכס של הלקוח, וצריכה בדיקה אנושית. */
function AiImageBox({ clientId, seed, onImage }: { clientId: string; seed: string; onImage: (i: { id: string; url: string }) => void }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ subject: seed, scene: '', mood: '', shape: 'landscape', modest: true, style: 'photo' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [last, setLast] = useState<{ id: string; url: string } | null>(null);
  const elapsed = useElapsed(busy);
  const abort = useRef<AbortController | null>(null);
  const prompt = [f.subject && `נושא: ${f.subject}.`, f.scene && `סצנה ורקע: ${f.scene}.`, f.mood && `אווירה וסגנון: ${f.mood}.`].filter(Boolean).join(' ');
  const gen = async () => {
    setBusy(true); setErr(null);
    const ac = new AbortController(); abort.current = ac;
    const timer = setTimeout(() => ac.abort(), 150_000);
    try {
      const res = await fetch('/api/quick/generate-image', { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ clientId, prompt, shape: f.shape, modest: f.modest, style: f.style }), signal: ac.signal });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.error ?? `שגיאה ${res.status}`);
      setLast(body); onImage(body);
    } catch (e: any) {
      setErr(e?.name === 'AbortError' ? 'ההמתנה נקטעה (או בוטלה). אם זה קורה שוב ושוב, ייתכן שהאינטרנט או הסינון חוסמים בקשות ארוכות. נסו שוב, או העלו תמונה בעצמכם.' : e.message);
    } finally { clearTimeout(timer); setBusy(false); }
  };
  if (!open) return <button type="button" className="btn" onClick={() => setOpen(true)} style={{ alignSelf: 'flex-start' }}><Icon name="wand" size={18} />ליצור תמונה ב-AI</button>;
  return (
    <fieldset className="card stack" style={{ background: 'var(--surface-2)' }}>
      <legend className="small" style={{ fontWeight: 700 }}>יצירת תמונה ב-AI</legend>
      <Field label="מה בתמונה? *" hint="לדוגמה: משפחה חרדית צעירה עם שני ילדים"><input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} /></Field>
      <Field label="סצנה ורקע" hint="לדוגמה: מרפסת מוארת בדירה חדשה, נוף ירוק"><input value={f.scene} onChange={(e) => setF({ ...f, scene: e.target.value })} /></Field>
      <Field label="אווירה וסגנון" hint="לדוגמה: חם, צילום מקצועי, אור יום"><input value={f.mood} onChange={(e) => setF({ ...f, mood: e.target.value })} /></Field>
      <div className="row">
        <Field label="סגנון"><select value={f.style} onChange={(e) => setF({ ...f, style: e.target.value })}><option value="photo">צילום ריאליסטי</option><option value="illustration">איור</option></select></Field>
        <Field label="צורה"><select value={f.shape} onChange={(e) => setF({ ...f, shape: e.target.value })}><option value="landscape">רחבה</option><option value="square">ריבועית</option><option value="portrait">גבוהה</option></select></Field>
        <label className="row small"><input type="checkbox" checked={f.modest} onChange={(e) => setF({ ...f, modest: e.target.checked })} /> הקפדה על צניעות לפי תקני הקהילה החרדית</label>
      </div>
      <ErrorBox error={err} />
      <div className="row">
        <button type="button" className="btn primary" disabled={busy || f.subject.trim().length < 3} onClick={gen}>{busy ? <><span className="spinner" /> יוצר… {elapsed} שניות</> : 'יצירת תמונה'}</button>
        {busy && <button type="button" className="btn" onClick={() => abort.current?.abort()}>ביטול</button>}
        <button type="button" className="btn ghost" onClick={() => setOpen(false)}>סגירה</button>
      </div>
      {last && <div className="alert warn"><img src={last.url} alt="התמונה שנוצרה" style={{ maxWidth: 260, display: 'block', marginBottom: 8, borderRadius: 6 }} />התמונה נבחרה לבאנר. <strong>בדקו אותה בעין לפני שימוש:</strong> צניעות, ידיים ופנים, ושאין בה טקסט שגוי. אפשר ליצור שוב.</div>}
    </fieldset>
  );
}
